// context/CartContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + action.payload.quantity }
              : item
          )
        };
      }
      return {
        ...state,
        items: [...state.items, action.payload]
      };

    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ).filter(item => item.quantity > 0)
      };

    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload.id)
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };

    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload || []
      };

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('easemyhome_cart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        dispatch({ type: 'LOAD_CART', payload: cartData });
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('easemyhome_cart', JSON.stringify(state.items));
  }, [state.items]);

  const addToCart = (service, quantity = 1) => {
    const cartItem = {
      id: service.name,
      name: service.name,
      description: service.description,
      basePrice: service.price || service.calculatedPrice || service.basePrice || 0,
      duration: service.duration,
      warranty: service.warranty,
      category: service.category,
      includes: service.includes,
      quantity: quantity,
      selectedVariant: service.selectedVariant || null
    };
    dispatch({ type: 'ADD_TO_CART', payload: cartItem });
  };

  const updateQuantity = (id, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });
  };

  const removeFromCart = (id) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: { id } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => {
      const itemTotal = calculateItemTotal(item);
      return total + itemTotal;
    }, 0);
  };

  const getCartItemCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  const calculateItemTotal = (item) => {
    // For variant services, the price is already set from the variant
    // Just multiply by quantity without applying additional discounts
    if (item.selectedVariant || item.name.includes(' - ')) {
      return item.basePrice * item.quantity;
    }
    
    // For other services, apply quantity pricing with discounts
    const pricing = getQuantityPricing(item.name, item.basePrice);
    const priceForQuantity = pricing[item.quantity] || pricing[1] * item.quantity;
    return priceForQuantity;
  };

  const getQuantityPricing = (serviceName, basePrice) => {
    // Special pricing for specific services
    const specialPricing = {
      'Split AC Jet Service': {
        1: 599,
        2: 1098, // ₹100 off from 1198
        3: 1497, // ₹300 off from 1797
        4: 1896  // ₹500 off from 2396
      },
      'Split AC Installation': {
        1: 1499,
        2: 2499, // 500 off
        3: 3699, // 800 off  
        4: 4799  // 1200 off
      },
      'Split AC Uninstall': {
        1: 599,
        2: 999,
        3: 1399,
        4: 1799
      },
      'House Cleaning': {
        1: 4599, // Unfurnished apartment
        2: 8599, // Small discount for 2
        3: 12299, // Better discount for 3
        4: 15699  // Best discount for 4
      },
      'Essential Home Cleaning': {
        1: 4599, // 1 BHK
        2: 4999, // 2 BHK
        3: 5899, // 3 BHK
        4: 7299, // 4 BHK
        5: 8099  // 5 BHK
      },
      'Complete Kitchen Cleaning': {
        1: 1599,
        2: 2999, // Small discount
        3: 4299, // Better discount
        4: 5499  // Best discount
      }
    };

    // Check for exact match first
    if (specialPricing[serviceName]) {
      return specialPricing[serviceName];
    }

    // Check if service name starts with any special pricing key (for variant services)
    for (const key in specialPricing) {
      if (serviceName.startsWith(key)) {
        return specialPricing[key];
      }
    }

    // Default quantity pricing with discounts
    return {
      1: basePrice,
      2: Math.round(basePrice * 1.8), // 10% discount
      3: Math.round(basePrice * 2.6), // 13% discount
      4: Math.round(basePrice * 3.3)  // 17% discount
    };
  };

  const getDiscountAmount = (serviceName, basePrice, quantity, item) => {
    if (quantity <= 1) return 0;
    
    // For variant services, check if the variant itself has a discount
    if (item && item.selectedVariant && item.selectedVariant.discount) {
      // The discount is already included in the variant price
      // If quantity > 1, there's no additional discount
      return 0;
    }
    
    // For variant services identified by name, no additional discount
    if (serviceName.includes(' - ')) {
      return 0;
    }
    
    const pricing = getQuantityPricing(serviceName, basePrice);
    const regularPrice = basePrice * quantity;
    const discountedPrice = pricing[quantity] || regularPrice;
    
    return Math.max(0, regularPrice - discountedPrice);
  };

  const value = {
    items: state.items,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartItemCount,
    calculateItemTotal,
    getQuantityPricing,
    getDiscountAmount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};